import { Feather, CalendarDays, Sparkles, CloudSun, Box, Map, NotebookPen, BarChart3, Cloud, Target, BookOpen } from 'lucide-react';

const SCREENSHOTS = {
  recommendations: 'https://customer-assets.emergentagent.com/job_fishing-companion-4/artifacts/uuh0q985_fly1.JPG',
  hatch: 'https://customer-assets.emergentagent.com/job_fishing-companion-4/artifacts/jqx4y1j9_fly2.JPG',
  weather: 'https://customer-assets.emergentagent.com/job_fishing-companion-4/artifacts/pwb92xbl_fly3.JPG',
  patterns: 'https://customer-assets.emergentagent.com/job_fishing-companion-4/artifacts/4cwgujms_fly4.JPG',
};
const PHOTOS = {
  flybox: 'https://images.unsplash.com/photo-1689709105115-e11810b6c425?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  trip: 'https://images.unsplash.com/photo-1610194435904-cdd826cc99b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  logbook: 'https://images.unsplash.com/photo-1688799401080-a4e6fe51a861?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  analytics: 'https://images.unsplash.com/photo-1504997565900-67b8c1e70940?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
};

const REGIONS = ['Ontario', 'British Columbia', 'Alberta', 'Montana', 'Colorado', 'Pennsylvania'];
const LIFE_STAGES = ['Larvae', 'Nymph', 'Emerger', 'Adult'];
const SPECIES = ['Rainbow Trout', 'Brown Trout', 'Brook Trout', 'Steelhead', 'Cutthroat', 'Bull Trout', '+10 more'];

const SampleRecCard = () => (
  <div className="mt-8 bg-[#151b21] rounded-2xl overflow-hidden border border-white/10 shadow-xl">
    <div className="bg-gradient-to-r from-[#0c4f7d] to-[#137dc5] px-5 py-3">
      <h4 className="text-white text-sm font-semibold">Today's Recommendation &bull; Bow River, Alberta</h4>
    </div>
    <div className="p-5">
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <div className="bg-[#0F1419] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Cloud className="w-4 h-4 text-[#3aa0e0]" /><span className="font-semibold text-white text-sm">Conditions</span></div>
          <p className="text-slate-400 text-xs">Water: 14&deg;C<br />Overcast, light rain<br />2:00 PM</p>
        </div>
        <div className="bg-[#0F1419] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-[#3aa0e0]" /><span className="font-semibold text-white text-sm">Recommended Fly</span></div>
          <p className="text-slate-400 text-xs"><strong className="text-slate-200">Blue Wing Olive</strong><br />Size #18-20, Emerger<br />Olive/gray body</p>
        </div>
        <div className="bg-[#0F1419] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><BookOpen className="w-4 h-4 text-[#3aa0e0]" /><span className="font-semibold text-white text-sm">Why It Works</span></div>
          <p className="text-slate-400 text-xs">BWO hatch peaks at 12-15&deg;C. Overcast triggers surface activity. Fish the film.</p>
        </div>
      </div>
      <div className="bg-[#137dc5]/10 rounded-xl p-4 border-l-4 border-[#137dc5]">
        <p className="text-slate-300 text-xs leading-relaxed"><strong className="text-[#3aa0e0]">Pro Tip:</strong> Cast upstream into current seams. Let the fly drift naturally in the surface film. Watch for subtle rises &mdash; BWO takes are soft. Use 5X tippet and a long leader (12-14 ft) for a drag-free drift.</p>
      </div>
    </div>
  </div>
);

const HatchExtra = () => (
  <div className="mt-6 space-y-4">
    <div>
      <p className="text-sm font-semibold text-slate-300 mb-2">Regions covered</p>
      <div className="flex flex-wrap gap-2">
        {REGIONS.map((r) => (
          <span key={r} className="text-sm bg-[#137dc5]/10 border border-[#137dc5]/30 text-[#3aa0e0] px-3 py-1 rounded-full">{r}</span>
        ))}
      </div>
    </div>
    <div>
      <p className="text-sm font-semibold text-slate-300 mb-2">Life stages tracked</p>
      <div className="flex flex-wrap items-center gap-2">
        {LIFE_STAGES.map((s, i) => (
          <span key={s} className="flex items-center gap-2 text-sm text-slate-400">
            <span className="bg-[#1E2328] border border-white/10 px-3 py-1 rounded-full text-slate-200">{s}</span>
            {i < LIFE_STAGES.length - 1 && <span className="text-[#137dc5]">&rarr;</span>}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const SpeciesExtra = () => (
  <div className="mt-6">
    <p className="text-sm font-semibold text-slate-300 mb-2">Log 16 species, including</p>
    <div className="flex flex-wrap gap-2">
      {SPECIES.map((s) => (
        <span key={s} className="text-sm bg-[#1E2328] border border-white/10 text-slate-200 px-3 py-1 rounded-full">{s}</span>
      ))}
    </div>
  </div>
);

const FEATURES = [
  { Icon: Feather, title: 'Fly Patterns Database', kind: 'screenshot', image: SCREENSHOTS.patterns,
    description: 'Browse 70+ expertly curated fly patterns with HD images, hook sizes, recommended techniques, and tying instructions. From Adams Dry Flies to Woolly Buggers, find the right pattern for any situation.' },
  { Icon: CalendarDays, title: 'Regional Hatch Calendar', kind: 'screenshot', image: SCREENSHOTS.hatch, Extra: HatchExtra,
    description: 'Real-time hatch activity for 7+ regions including Ontario, British Columbia, Alberta, Montana, Colorado, and Pennsylvania. See which insects are active each month with life-stage images \u2014 from larvae to adult \u2014 so you always know what the fish are feeding on.' },
  { Icon: Sparkles, title: 'AI-Powered Fly Recommendations', kind: 'screenshot', image: SCREENSHOTS.recommendations, Extra: SampleRecCard,
    description: 'Get intelligent fly suggestions based on your location, current weather conditions, water temperature, and active hatches. Our AI analyzes multiple data sources to recommend the best flies for right now.' },
  { Icon: CloudSun, title: 'Weather & Conditions Analysis', kind: 'screenshot', image: SCREENSHOTS.weather,
    description: 'Real-time weather data including wind speed, barometric pressure, water temperature, and an AI-powered fishing quality score. Know before you go whether conditions are prime.' },
  { Icon: Box, title: 'My Fly Box', kind: 'photo', image: PHOTOS.flybox,
    description: 'Build your personal digital fly box. Track which flies you own for quick reference on the water. Never forget what\u2019s in your vest again.' },
  { Icon: Map, title: 'Trip Planner', kind: 'photo', image: PHOTOS.trip,
    description: 'Plan upcoming trips with date selection and location-based previews of expected hatch activity and weather conditions.' },
  { Icon: NotebookPen, title: 'Fishing Logbook', kind: 'photo', image: PHOTOS.logbook, Extra: SpeciesExtra,
    description: 'Record every outing with GPS location, date, weather conditions, multiple flies used per trip, and detailed fish catches \u2014 species, length, and weight. Rate your flies and track what\u2019s working.' },
  { Icon: BarChart3, title: 'Analytics Dashboard', kind: 'photo', image: PHOTOS.analytics,
    description: 'Visualize your fishing data. Track catch statistics, fly performance over time, and trip history. See the patterns behind your fishing success.' },
];

const FeatureRow = ({ feature, index }) => {
  const { Icon, title, description, image, kind, tag, Extra } = feature;
  const reversed = index % 2 === 1;
  return (
    <div className="py-14 border-b border-white/5 last:border-b-0">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className={reversed ? 'lg:order-2' : ''}>
          {tag && (
            <span className="inline-block mb-4 text-xs font-bold uppercase tracking-wider text-[#3aa0e0] bg-[#137dc5]/10 border border-[#137dc5]/40 px-3 py-1 rounded-full">{tag}</span>
          )}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#137dc5]/15 flex items-center justify-center flex-shrink-0">
              <Icon className="w-6 h-6 text-[#3aa0e0]" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white">{title}</h3>
          </div>
          <p className="text-lg text-slate-400 leading-relaxed">{description}</p>
          {Extra && <Extra />}
        </div>
        <div className={`flex justify-center ${reversed ? 'lg:order-1' : ''}`}>
          {kind === 'screenshot' ? (
            <div className="relative">
              <div className="absolute -inset-4 bg-[#137dc5]/10 blur-3xl rounded-full"></div>
              <img src={image} alt={`${title} screen in the FlyFinder app`} loading="lazy" className="relative w-full max-w-[280px] rounded-[2rem] border-4 border-[#1E2328] shadow-2xl" />
            </div>
          ) : (
            <img src={image} alt={title} loading="lazy" className="w-full rounded-2xl border border-white/10 shadow-2xl object-cover aspect-[4/3]" />
          )}
        </div>
      </div>
    </div>
  );
};

const FeatureSections = () => (
  <section id="features" className="py-20 bg-[#0F1419]" data-testid="feature-sections">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-6">
        <span className="text-sm font-bold uppercase tracking-wider text-[#3aa0e0]">Everything you need on the water</span>
        <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">One App, Your Whole Fishing Brain</h2>
        <p className="text-lg text-slate-400">From what's hatching right now to a full record of every catch &mdash; FlyFinder brings the knowledge of a seasoned guide to your pocket.</p>
      </div>
      <div>
        {FEATURES.map((feature, index) => (
          <FeatureRow key={feature.title} feature={feature} index={index} />
        ))}
      </div>
    </div>
  </section>
);

export default FeatureSections;
